import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as yup from "yup";
import { changeForgetPasswordRoute } from "../../routes/apiRoutes";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { loginRoute } from "../../routes/reactRoutes";
import UnAuthorized from "../commonControls/UnAuthorized";
import { LogoAndText } from "../../theme/JaziTheme";
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axiosInstance from "../../axios/axiosInstance";
import { t } from "i18next";
import FlipLanguageSwitcher from "./FlipLanguageSwitcher";

const ResetPassword = (props) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  useEffect(() => {
    document.title = props.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const { state } = useLocation();
  const otp = state?.otp;
  const textFieldStyle = {
    background: theme.palette.componentBackgrounds.new,
    width: "100%",
    my: 1.75,
    borderRadius: 1,
    border: "none",
    height: 40,
  };

  const handlePasswordVisibility = (field) => {
    if (field === "newPassword") {
      setPasswordVisible((prevState) => {
        return { ...prevState, newPassword: !prevState.newPassword };
      });
    } else if (field === "confirmPassword") {
      setPasswordVisible((prevState) => {
        return { ...prevState, confirmPassword: !prevState.confirmPassword };
      });
    }
  };

  const schema = yup.object({
    newPassword: yup
      .string()
      .min(8, t("passwordyupmin"))
      .max(20, t("passwordyupmax"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&]).{8,}$/,
        t("passwordyup")
      )
      .required(t("passwordisrequired")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], t("confirmpasswordsame"))
      .required(t("confirmpasswordrequired")),
  });

  useEffect(() => {
    Object.keys(formik.touched).forEach((field) => {
      try {
        formik.validateField(field);
      } catch {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.direction]);

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Post signup data to server
        const { data } = await axiosInstance.post(
          `${changeForgetPasswordRoute}`,
          {
            OTP: otp,
            password: values.newPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (data.statusCode === 200 || data.statusCode === 201) {
          toast.dismiss();
          toast.success(t("passwordupdatesuccess"), { autoClose: 1000 });
          setLoading(false);
          navigate(loginRoute);
        } else {
          toast.dismiss();
          toast.error(t("unexpectedserverresponse"), { autoClose: false });
          formik.resetForm();
          setLoading(false);
        }
      } catch (error) {
        toast.dismiss();
        toast.error(t("passwordreseterror"), {
          autoClose: false,
        });
        formik.resetForm();
        setLoading(false);
      }
    },
  });

  const outerBox = {
    background: theme.palette.whiteblack.main,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    //border: `0.5px solid ${theme.palette.componentBackgrounds.main}`,
    borderRadius: 1,
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
    py: 5,
    px: 2,
    maxWidth: 500,
    maxHeight: 525,
    width: "100%",
  };
  const formStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "90%",
    gap: 2.5,
  };
  const formStyle2 = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "100%",
  };
  const rootStyle = {
    display: "grid",
    placeItems: "center",
    height: `100vh`,
    background: theme.palette.primary.main,
    backgroundImage: 'url("background.webp")',
    backgroundSize: "cover",
  };

  return otp ? (
    <div dir={theme.direction} style={rootStyle}>
      <Paper elevation={1} sx={outerBox}>
        <Box
          sx={{ display: "flex", justifyContent: "center", mb: 2, zoom: 0.8 }}
        >
          <LogoAndText />
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "90%",
            textAlign: "left",
            alignItems: "center",
            mb: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 800,
              textAlign: "left",
              alignSelf: "center",
              flexGrow: 1,
            }}
          >
            {t("resetpassword")}
          </Typography>
          <FlipLanguageSwitcher />
        </Box>
        <form style={formStyle2} onSubmit={formik.handleSubmit}>
          <Box sx={formStyle}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                mt: 4.6,
                alignItems: "center",
              }}
            >
              <TextField
                id="profile-new-password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                type={!passwordVisible.newPassword ? "password" : "text"}
                name="newPassword"
                // placeholder={t("newpassword")}
                label={t("newpassword")}
                sx={textFieldStyle}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        aria-label="toggle confirm password visibility"
                        onClick={() => handlePasswordVisibility("newPassword")}
                        edge="end"
                      >
                        {passwordVisible.newPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="profile-confirm-password"
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                size="small"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="confirmPassword"
                sx={textFieldStyle}
                type={!passwordVisible.confirmPassword ? "password" : "text"}
                label={t("confirmpassword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          handlePasswordVisibility("confirmPassword")
                        }
                        edge="end"
                      >
                        {passwordVisible.confirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Box sx={{ width: "90%" }}>
            <LoadingButton
              type="submit"
              loading={loading}
              variant="contained"
              sx={{
                width: "100%",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                my: 2.5,
                py: 1.5,
              }}
            >
              <span>{t("updatepassword")}</span>
            </LoadingButton>
          </Box>
        </form>
      </Paper>
    </div>
  ) : (
    <Box sx={{ height: "100vh" }}>
      <UnAuthorized />
    </Box>
  );
};
export default ResetPassword;
